import type {
  MessengerPublicConfig,
  UrlCacheableConfig,
} from "@deskpro-product/messenger-api-schemas/messenger/config";
import { UrlCacheableConfigSchema } from "@deskpro-product/messenger-api-schemas/messenger/config";
import getConfigClient from "./getConfigClient";
import { Value } from "@deskpro-product/messenger-api-schemas/typebox";
import type { JwtPayload } from "jwt-decode";
import jwtDecode from "jwt-decode";

export default async function getMessengerConfig({
  tenantId,
  messengerId,
  host,
  platform,
  messengerToken,
}: {
  tenantId: string;
  messengerId: string;
  platform?: NonNullable<UrlCacheableConfig["platform"]>;
  host?: string;
  messengerToken: string;
}): Promise<{ config: MessengerPublicConfig; mediaBaseUrl: string }> {
  const response = await getConfigClient({
    tenantId,
    messengerId,
    host,
    platform,
    messengerToken,
  });
  return {
    config: { ...response.config, revId: String(response.id) },
    mediaBaseUrl: response.mediaBaseUrl,
  };
}

export const parseHostPageConfig = (hostConfig: string): UrlCacheableConfig | undefined => {
  // d is the default value for the hostConfig route param, used when there is no hostConfig to avoid the route segment being optional
  if (!hostConfig || hostConfig === "d") {
    return;
  }

  try {
    const data = JSON.parse(decodeURIComponent(hostConfig));

    if (Value.Check(UrlCacheableConfigSchema, data)) {
      return Value.Decode(UrlCacheableConfigSchema, data);
    } else {
      console.dir(Value.Errors(UrlCacheableConfigSchema, data), { depth: 2 });
      console.dir(data, { depth: 2 });
    }
  } catch (e) {
    console.error("parseHostPageConfig", e, hostConfig);
  }
};

/**
 * Parse and return the messenger token from the host page config
 * The messenger token is used to verify the context that the messenger is running in.
 * Currently this will only be present for messenger running inside Help Center or the admin preview.
 * For admin preview the token will encode the latest revision ID of the messenger config
 */
export const getMessengerToken = (hostConfig: string): string => {
  return parseHostPageConfig(hostConfig)?.messengerToken || "";
};

type MessengerJwtPayload = Omit<JwtPayload, "iss"> & {
  dpRevId?: string;
  iss: "admin-api" | "help-center";
};

/**
 * Be warned that we are not verifying the token here, only decoding it.
 * Use this as a helper but do not rely on it for anything auth or security related.
 */
export function getDecodedMessengerToken(messengerToken: string): MessengerJwtPayload | undefined {
  if (!messengerToken) return;
  try {
    if (!messengerToken) return;
    return jwtDecode<MessengerJwtPayload>(messengerToken);
  } catch (e) {
    console.error("decodeDpRevId error:", e);
  }
}

/**
 * Reads the issuer of the token to determine if we are in admin preview mode
 * Be warned that we are not verifying the token here, only reading the issuer.
 * Use this as a helper but do not rely on it for anything auth or security related.
 */
export function isAdminPreviewToken(messengerToken: string): boolean {
  const decoded = getDecodedMessengerToken(messengerToken);
  return decoded?.iss === "admin-api";
}
